<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import HomeBackground from "@/components/HomeBackground.vue";
import LanguageMenu from "@/components/LanguageMenu.vue";
import { useLanguageStore } from "@/stores/languageStore.js";
import { useProgressStore } from "@/stores/progressStore.js";
import { useSessionStore } from "@/stores/sessionStore.js";
import { gameRuntimeBridge } from "@/game/bridge/runtimeBridge.js";

const router = useRouter();
const languageStore = useLanguageStore();
const progressStore = useProgressStore();
const sessionStore = useSessionStore();
const actionError = ref("");
const busy = ref(false);
const energyView = ref({
  current: null,
  max: null,
  cost: null,
  isFull: false,
  countdownText: ""
});
let energyTimer = null;
let energyRefreshInFlight = false;

const continueLevel = computed(() => {
  const remoteLevel = Number(sessionStore.home?.current_level_no);
  if (sessionStore.backendEnabled && Number.isFinite(remoteLevel) && remoteLevel > 0) return remoteLevel;
  return progressStore.highestUnlocked;
});

/*
 * HOME_OFFICIAL_PLAY_V1
 *
 * Home PLAY and Home progress always represent
 * the official campaign. Workshop remains
 * available only through its own explicit entry.
 */
const challengeProgressText =
  computed(
    () =>
      languageStore.t(
        "systemMapCurrentProgress",
        {
          level:
            continueLevel.value
        }
      )
  );


function refreshEnergyView() {
  const next = gameRuntimeBridge.getEnergy();
  if (!next) return null;
  energyView.value = { ...energyView.value, ...next };
  return next;
}

function handleRuntimeReady() {
  refreshEnergyView();
}


async function refreshEnergyFromBackend() {
  if (
    !sessionStore.backendEnabled
    || energyRefreshInFlight
  ) {
    return null;
  }

  energyRefreshInFlight = true;

  try {
    const home =
      await sessionStore.refreshHome();

    refreshEnergyView();

    return home;
  } catch {
    return null;
  } finally {
    energyRefreshInFlight = false;
  }
}


async function tickEnergyView() {
  const next = refreshEnergyView();

  if (
    !next
    || !sessionStore.backendEnabled
    || next.isFull
  ) {
    return;
  }

  if (
    Number(next.nextRecoveryMs) <= 0
  ) {
    await refreshEnergyFromBackend();
  }
}


function handleVisibilityChange() {
  if (
    document.visibilityState
    === "visible"
  ) {
    void refreshEnergyFromBackend();
  }
}

const energyReady = computed(() =>
  energyView.value.current !== null && Number.isFinite(Number(energyView.value.current))
);
const hasEnoughEnergy = computed(() => {
  if (!energyReady.value) return true;
  return Number(energyView.value.current) >= Number(energyView.value.cost || 0);
});

onMounted(async () => {
  progressStore.refresh();
  window.addEventListener("bricksbeater:runtime-ready", handleRuntimeReady);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  refreshEnergyView();
  energyTimer =
    window.setInterval(
      () => {
        void tickEnergyView();
      },
      1000
    );
  if (sessionStore.backendEnabled) {
    await refreshEnergyFromBackend();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("bricksbeater:runtime-ready", handleRuntimeReady);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  if (energyTimer) window.clearInterval(energyTimer);
});

async function play() {
  busy.value = true;
  actionError.value = "";
  try {
    const energy = refreshEnergyView();
    if (!energy) {
      actionError.value = languageStore.t("startError");
      return;
    }
    if (Number(energy.current) < Number(energy.cost)) {
      actionError.value = languageStore.t("energyInsufficient", {
        current: energy.current,
        cost: energy.cost
      });
      return;
    }

    let level = continueLevel.value;
    if (sessionStore.backendEnabled) {
      const playInfo = await sessionStore.startCampaign();
      level = Number(playInfo?.level_no) || level;
    }
    const preloaded = gameRuntimeBridge.isReady()
      ? await gameRuntimeBridge.startLevel(level, { consumeEnergy: false })
      : false;
    await router.push({
      name: "game",
      params: { level },
      query: {
        entry: "continue",
        ...(preloaded ? { preloaded: "1" } : {})
      }
    });
  } catch (error) {
    actionError.value = error?.message || languageStore.t("startError");
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section class="home">
    <HomeBackground />

    <!-- 语言入口独立悬浮，不再占用功能按钮网格。 -->
    <div class="energy-anchor" :class="{ low: energyReady && !hasEnoughEnergy }">
      <span class="energy-bolt" aria-hidden="true">⚡</span>
      <span class="energy-copy">
        <strong v-if="energyReady">{{ energyView.current }}/{{ energyView.max }}</strong>
        <strong v-else>--/--</strong>
        <small>{{ energyReady ? energyView.countdownText : languageStore.t('energyLoading') }}</small>
      </span>
    </div>

    <div class="language-anchor">
      <LanguageMenu />
    </div>

    <div class="home-content">
      <div class="hero">
        <h1>{{ languageStore.t('title') }}</h1>

        <div class="ball-art">
          <img src="/assets/zhuyexiaoqiu.jpg" alt="" />
        </div>
      </div>

      <div class="primary-action">
        <button class="play-button" type="button" :disabled="busy" @click="play">
          <span class="play-icon" aria-hidden="true"></span>
          <span class="play-label">{{ languageStore.t('play') }}</span>
        </button>

      </div>      <!-- HOME_VERTICAL_ENTRY_LIST_V1_START -->
      <div class="home-entry-list">
        <button
        class="feature-tile vertical-entry"
        type="button"
        @click="router.push({ name: 'workshop' })"
        >
        <span class="feature-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
        <path d="m4 20 5.2-1.2L19.4 8.6a1.8 1.8 0 0 0 0-2.6L18 4.6a1.8 1.8 0 0 0-2.6 0L5.2 14.8 4 20Z" />
        <path d="m13.8 6.2 4 4" />
        <path d="M7 4.5v3M5.5 6h3M19.5 14.5v3M18 16h3" />
        </svg>
        </span>
        <span class="feature-entry-label">{{ languageStore.t('creativeWorkshop') }}</span>
        </button>
        <button
        class="feature-tile vertical-entry"
        type="button"
        @click="router.push({ name: 'levels' })"
        >
        <span class="feature-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
        <path d="M3 4.5h5.5c2 0 3.5 1.3 3.5 3.1V20c0-1.7-1.5-3-3.5-3H3V4.5Z" />
        <path d="M21 4.5h-5.5c-2 0-3.5 1.3-3.5 3.1V20c0-1.7 1.5-3 3.5-3H21V4.5Z" />
        </svg>
        </span>
        <span class="feature-entry-label">{{ languageStore.t('systemMapName') }}</span>
        </button>
        <button
        class="feature-tile vertical-entry"
        type="button"
        @click="router.push({ name: 'leaderboard' })"
        >
        <span class="feature-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
        <path d="M8 20v-6h4v6M4 20v-3h4v3M12 20V9h4v11M16 20v-8h4v8" />
        <path d="M4 6.5 8.5 3 12 5.5 18.5 2" />
        </svg>
        </span>
        <span class="feature-entry-label">{{ languageStore.t('leaderboardHint') }}</span>
        </button>
        <button
        class="feature-tile vertical-entry"
        type="button"
        @click="router.push({ name: 'skins' })"
        >
        <span class="feature-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
        <path d="M8 4 4 6.5 6.2 10l2.3-1.2V20h7V8.8l2.3 1.2L20 6.5 16 4c-.9 1.5-2.2 2.3-4 2.3S8.9 5.5 8 4Z" />
        </svg>
        </span>
        <span class="feature-entry-label">{{ languageStore.t('skinsTitle') }}</span>
        </button>
        <button
        class="feature-tile vertical-entry"
        type="button"
        @click="router.push({ name: 'settings' })"
        >
        <span class="feature-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
        <path d="M12 2.5 13.2 5a7.2 7.2 0 0 1 1.8.8l2.6-.9 1.5 1.5-.9 2.6c.4.6.7 1.2.8 1.8l2.5 1.2v2L19 15.2a7.2 7.2 0 0 1-.8 1.8l.9 2.6-1.5 1.5-2.6-.9a7.2 7.2 0 0 1-1.8.8L12 23.5 10.8 21a7.2 7.2 0 0 1-1.8-.8l-2.6.9-1.5-1.5.9-2.6a7.2 7.2 0 0 1-.8-1.8L2.5 14v-2L5 10.8A7.2 7.2 0 0 1 5.8 9l-.9-2.6 1.5-1.5 2.6.9a7.2 7.2 0 0 1 1.8-.8L12 2.5Z" />
        <circle cx="12" cy="13" r="3.2" />
        </svg>
        </span>
        <span class="feature-entry-label">{{ languageStore.t('settingsTitle') }}</span>
        </button>
      </div>
      <!-- HOME_VERTICAL_ENTRY_LIST_V1_END -->

      <p v-if="actionError" class="error-text">{{ actionError }}</p>
    </div>
  </section>
</template>

<style scoped>
.home {
  position: relative;
  isolation: isolate;
  min-height: 100dvh;
  width: min(100%, 620px);
  margin: 0 auto;
  padding: 18px 18px 28px;
  overflow: visible;
}

.energy-anchor {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 30;
  min-width: 94px;
  height: 48px;
  padding: 6px 12px 6px 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(82,246,232,.62);
  border-radius: 16px;
  color: #fff;
  background: linear-gradient(180deg, rgba(4,48,67,.92), rgba(3,28,46,.88));
  box-shadow: 0 0 14px rgba(82,246,232,.14), inset 0 0 12px rgba(82,246,232,.04);
  backdrop-filter: blur(8px);
}

.energy-anchor.low {
  border-color: rgba(255,116,128,.72);
  box-shadow: 0 0 14px rgba(255,93,110,.14), inset 0 0 12px rgba(255,93,110,.04);
}

.energy-bolt {
  color: #ffe34f;
  font-size: 20px;
  line-height: 1;
  filter: drop-shadow(0 0 4px rgba(255,214,68,.48));
}

.energy-copy {
  display: grid;
  gap: 1px;
  min-width: 0;
}

.energy-copy strong {
  font-size: 14px;
  font-weight: 950;
  line-height: 1;
  letter-spacing: .02em;
}

.energy-copy small {
  color: rgba(255,224,112,.88);
  font-size: 9.5px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

.energy-anchor.low .energy-copy small { color: #ff9aa7; }

.language-anchor {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 30;
}

.home-content {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: calc(100dvh - 46px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.hero {
  width: 100%;
  text-align: center;
  transform: translateY(-14px);
  margin-bottom: 70px;
}

h1 {
  margin: 0;
  color: #fff;
  font-size: clamp(47px, 10.8vw, 72px);
  font-weight: 950;
  line-height: .91;
  letter-spacing: .015em;
  white-space: pre-line;
  text-shadow: 0 0 6px rgba(255,255,255,.25), 0 0 28px rgba(82,246,232,.28);
}

.hero-subtitle {
  margin: 10px 0 15px;
  color: rgba(224,255,252,.78);
  font-size: clamp(11px, 2.7vw, 14px);
  font-weight: 700;
  letter-spacing: .12em;
  text-shadow: 0 0 12px rgba(82,246,232,.18);
}

.ball-art {
  display: none;
  
  width: 72px;
  height: 72px;
  margin: 20px auto 20px;
  overflow: hidden;
  border-radius: 50%;
  box-shadow: 0 0 22px rgba(23, 24, 24, 0.6);
  animation: hero-ball-spin 10s linear infinite;
}

.ball-art img { width: 100%; height: 100%; object-fit: cover; }

.primary-action {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.play-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 208px;
  height: 74px;
  padding: 0 23px;
  border: 1.5px solid rgba(82,246,232,.96);
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(180deg, rgba(16,118,134,.94), rgba(3,48,68,.95));
  box-shadow: 0 0 0 7px rgba(82,246,232,.07), 0 0 24px rgba(82,246,232,.28), inset 0 0 18px rgba(82,246,232,.08);
  animation: play-breathe 1.6s ease-in-out infinite;
}

.play-button:disabled { opacity: .62; }

.play-icon {
  width: 0;
  height: 0;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 15px solid #fff;
  filter: drop-shadow(0 0 5px rgba(82,246,232,.95));
  flex: 0 0 auto;
}

.play-label {
  font-size: clamp(22px, 5vw, 29px);
  font-weight: 900;
  line-height: 1;
  letter-spacing: .025em;
  white-space: nowrap;
}

.current-level-line {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin: 11px 0 17px;
  color: rgba(255,255,255,.86);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: .045em;
  text-shadow: 0 0 10px rgba(0,0,0,.75), 0 0 12px rgba(82,246,232,.18);
}

.current-level-line strong {
  color: #83fff4;
  font-size: 18px;
  font-weight: 950;
  letter-spacing: 0;
  text-shadow: 0 0 11px rgba(82,246,232,.58);
}

.challenge-progress-line strong {
  max-width: min(92vw, 420px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-size: 13px;
  line-height: 1.35;
  text-shadow: 0 1px 5px rgba(0,0,0,.86);
}

.feature-grid {
  width: min(100%, 390px);
  display: grid;
  gap: 10px;
  margin: 0 auto;
}

.feature-grid-primary {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 50px;
}

.feature-grid-secondary {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 10px;
}

.feature-tile {
  min-width: 0;
  border: 1px solid rgba(82,246,232,.48);
  color: #fff;
  background: linear-gradient(180deg, rgba(6,39,60,.90), rgba(3,24,42,.88));
  box-shadow: 0 0 13px rgba(82,246,232,.10), inset 0 0 0 1px rgba(255,255,255,.02);
  backdrop-filter: blur(8px);
}

.feature-tile:hover,
.feature-tile:focus-visible {
  border-color: rgba(82,246,232,.92);
  box-shadow: 0 0 18px rgba(82,246,232,.20);
  outline: none;
  transform: translateY(-1px);
}

.feature-tile.compact {
  height: 62px;
  padding: 6px 5px;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.feature-tile.wide {
  min-height: 63px;
  padding: 8px 10px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 9px;
  text-align: left;
}

.feature-icon {
  display: inline-flex;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
}

.feature-icon svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke: #fff;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.feature-grid-primary .feature-icon svg rect { fill: #fff; stroke: none; }

.feature-label {
  width: 100%;
  overflow: hidden;
  color: #fff;
  font-size: 14px;
  font-weight: 850;
  line-height: 1.05;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feature-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.feature-copy strong {
  overflow: hidden;
  font-size: 15px;
  line-height: 1.05;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feature-copy small {
  overflow: hidden;
  color: rgba(255,255,255,.55);
  font-size: 10.5px;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.error-text {
  max-width: 420px;
  margin: 14px auto 0;
  color: #ff9aa7;
  font-size: 13px;
  text-align: center;
}

@keyframes hero-ball-spin { to { transform: rotate(360deg); } }

@keyframes play-breathe {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 7px rgba(82,246,232,.07), 0 0 22px rgba(82,246,232,.22), inset 0 0 18px rgba(82,246,232,.08); }
  50% { transform: scale(1.045); box-shadow: 0 0 0 9px rgba(82,246,232,.10), 0 0 34px rgba(82,246,232,.36), inset 0 0 20px rgba(82,246,232,.10); }
}

@media (max-width: 420px) {
  .home { padding: 12px 14px 20px; }
  .energy-anchor { top: 12px; left: 12px; }
  .language-anchor { top: 12px; right: 12px; }
  .home-content { min-height: calc(100dvh - 32px); }
  .feature-grid { width: min(100%, 360px); gap: 8px; }
  .feature-grid-secondary { margin-top: 8px; }
  .feature-tile.wide { padding: 8px; gap: 7px; }
}

@media (max-height: 720px) {
  h1 { font-size: clamp(41px, 8.5vh, 59px); }
  .hero-subtitle { margin: 7px 0 10px; }
  .ball-art { width: 61px; height: 61px; margin-bottom: 12px; }
  .play-button { height: 66px; }
  .current-level-line { margin: 8px 0 12px; }
  .feature-tile.compact { height: 56px; }
  .feature-tile.wide { min-height: 56px; }
}

@media (prefers-reduced-motion: reduce) {
  .ball-art, .play-button { animation: none; }
}

/* HOME_ICON_LAYOUT_V1_START */

/* 上下两排图标使用相同尺寸 */
.feature-icon {
  width: 32px;
  height: 32px;
  overflow: visible;
}

/* 放大 SVG 内部图形，减少 viewBox 留白 */
.feature-icon svg {
  width: 100%;
  height: 100%;
  overflow: visible;
  transform: scale(1.12);
  transform-origin: center;
}

/* 创意工坊和排行榜内容整体居中 */
.feature-tile.wide {
  justify-content: center;
  gap: 14px;
  text-align: center;
}

/* 下排文字容器居中 */
.feature-copy {
  align-items: center;
  justify-content: center;
  gap: 0;
}

/* 字体高度与图标高度协调 */
.feature-copy strong {
  font-size: 18px;
  line-height: 32px;
  text-align: center;
}

/* SKIN_ICON_WIDTH_V1_START */
/* 单独横向拉宽皮肤图标 */
.feature-grid-primary .feature-tile:nth-child(3) .feature-icon svg {
  transform: scale(1.6, 1.12);
}

/* SKIN_ICON_WIDTH_V1_END */
/* HOME_ICON_LAYOUT_V1_END */

/* HOME_GRID_COMPACT_V1_START */

/*
 * 两排共用同一个总宽度。
 * 第一排三列，第二排两列，但左右边缘保持对齐。
 */
.feature-grid {
  width: min(100%, 280px);
  gap: 8px;
}

/* 压缩上下两排之间的距离 */
.feature-grid-secondary {
  margin-top: 15px;
}

/* 上下两排按钮使用相同高度 */
.feature-tile.compact,
.feature-tile.wide {
  height: 52px;
  min-height: 52px;
}

/* 第一排按钮内部空间 */
.feature-tile.compact {
  padding: 4px;
  border-radius: 14px;
}

/* 第二排按钮内部空间 */
.feature-tile.wide {
  padding: 5px 8px;
  border-radius: 14px;
}

/* 压缩整个按钮区域下方的页面留白 */
.home {
  padding-bottom: 12px;
}

/* HOME_GRID_COMPACT_V1_END */

/* HOME_ENERGY_PLAIN_V1_START */

/*
 * 能量区域和语言按钮使用相同的垂直高度。
 * 两者原本已经使用相同的 top 值，因此中心会对齐。
 */
.energy-anchor {
  min-width: 0;
  height: 40px;
  padding: 0;
  gap: 8px;

  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
}

/* 低能量状态也不重新显示边框和阴影 */
.energy-anchor.low {
  border: 0;
  background: transparent;
  box-shadow: none;
}

/*
 * 30/30 高度 14px；
 * 下方文字高度 10px；
 * 中间间距 2px；
 * 总高度为 26px。
 */
.energy-copy {
  display: grid;
  grid-template-rows: 14px 10px;
  align-content: center;
  gap: 4px;
}

/* 上方能量数字 */
.energy-copy strong {
  font-size: 14px;
  line-height: 14px;
}

/* 下方倒计时或“已满” */
.energy-copy small {
  font-size: 9.5px;
  line-height: 10px;
}

/* 闪电高度与右侧两行文字的总高度一致 */
.energy-bolt {
  font-size: 24px;
  line-height: 26px;
}

/* HOME_ENERGY_PLAIN_V1_END */

/* HOME_VIEWPORT_AND_FRAME_V1_START */

/* 将主页限制为一屏，取消整个主页上下滑动 */
.home {
  height: 100dvh;
  min-height: 100dvh;
  max-height: 100dvh;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: hidden;
  overscroll-behavior: none;
}

.home-content {
  height: 100%;
  min-height: 0;
  max-height: 100%;
  box-sizing: border-box;
}

/* 隐藏下方五个入口按钮的蓝色框、底色和阴影 */
.feature-tile,
.feature-tile:hover,
.feature-tile:focus-visible {
  border-color: transparent;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
}

/* HOME_VIEWPORT_AND_FRAME_V1_END */



/* HOME_VERTICAL_ENTRY_STYLE_V1_START */

/* 五个入口统一改为单列纵向排列 */
.home-entry-list {
  width: min(100%, 320px);
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0px;
}

/* 图标在左、文字在右，同时删除框、背景和阴影 */
.home-entry-list .feature-tile.vertical-entry,
.home-entry-list .feature-tile.vertical-entry:hover,
.home-entry-list .feature-tile.vertical-entry:focus-visible {
  width: 100%;
  height: 42px;
  min-height: 42px;
  margin: 0;
  padding: 4px 18px;

  display: grid;
  grid-template-columns: 38px minmax(0, 1fr);
  align-items: center;
  column-gap: 12px;

  border: 0;
  border-radius: 0;
  outline: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;

  color: #ffffff;
  text-align: left;
}

/* 保留轻微移动反馈，但不会重新出现蓝色框 */
.home-entry-list .feature-tile.vertical-entry:hover,
.home-entry-list .feature-tile.vertical-entry:focus-visible {
  transform: translateX(3px);
}

/* 五个图标统一大小、统一左侧位置 */
.home-entry-list .feature-icon {
  width: 30px;
  height: 30px;
  min-width: 30px;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.home-entry-list .feature-icon svg {
  width: 100%;
  height: 100%;
  transform: scale(1.08);
  transform-origin: center;
}

/* 第三个是皮肤图标，继续保留加宽效果 */
.home-entry-list .vertical-entry:nth-child(3) .feature-icon svg {
  transform: scaleX(1.35) scaleY(1.08);
}

/* 每个入口右侧的语言说明 */
.home-entry-list .feature-entry-label {
  min-width: 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* 高度较小的手机进一步压缩，防止主页重新出现滚动 */
@media (max-height: 700px) {
  .home-entry-list {
    margin-top: 2px;
    gap: 0;
  }

  .home-entry-list .feature-tile.vertical-entry,
  .home-entry-list .feature-tile.vertical-entry:hover,
  .home-entry-list .feature-tile.vertical-entry:focus-visible {
    height: 38px;
    min-height: 38px;
  }

  .home-entry-list .feature-icon {
    width: 27px;
    height: 27px;
    min-width: 27px;
  }

  .home-entry-list .feature-entry-label {
    line-height: 27px;
  }
}

/* HOME_VERTICAL_ENTRY_STYLE_V1_END */









/* HOME_ENTRY_SIZE_BALANCE_V2_START */

/* 开始游戏与第一项的距离保持22px */
.home-entry-list {
  --home-entry-top-gap: 22px;
  margin-top: var(--home-entry-top-gap);
}

/* 每一行稍微增高 */
.home-entry-list .feature-tile.vertical-entry,
.home-entry-list .feature-tile.vertical-entry:hover,
.home-entry-list .feature-tile.vertical-entry:focus-visible {
  height: 44px;
  min-height: 44px;

  /*
    左右列必须保持相同宽度，
    才能保证中间文字继续对准开始游戏按钮中心。
  */
  grid-template-columns: 32px fit-content(230px) 32px;
  column-gap: 10px;
  align-items: center;
}

/* 文字从19px放大到21px */
.home-entry-list .feature-entry-label {
  max-width: 230px;
  font-size: 18px;
  font-weight: 850;
  line-height: 30px;
  align-self: center;
}

/* 图标从26px放大到30px */
.home-entry-list .feature-icon {
  width: 30px;
  height: 30px;
  min-width: 30px;
  min-height: 30px;
  align-self: center;
  justify-self: center;
}

.home-entry-list .feature-icon svg {
  width: 30px;
  height: 30px;
  min-width: 30px;
  min-height: 30px;
  max-width: 30px;
  max-height: 30px;
}

/* 皮肤图标继续保持横向加宽 */
.home-entry-list .vertical-entry:nth-child(3) .feature-icon svg {
  transform: scaleX(1.25) scaleY(1);
  transform-origin: center;
}

/* 较窄屏幕稍微缩小，避免超出页面 */
@media (max-width: 380px) {
  .home-entry-list {
    --home-entry-top-gap: 18px;
  }

  .home-entry-list .feature-tile.vertical-entry,
  .home-entry-list .feature-tile.vertical-entry:hover,
  .home-entry-list .feature-tile.vertical-entry:focus-visible {
    height: 42px;
    min-height: 42px;
    grid-template-columns: 30px fit-content(200px) 30px;
    column-gap: 9px;
  }

  .home-entry-list .feature-entry-label {
    max-width: 200px;
    font-size: 20px;
    line-height: 28px;
  }

  .home-entry-list .feature-icon,
  .home-entry-list .feature-icon svg {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    max-width: 28px;
    max-height: 28px;
  }
}

/* HOME_ENTRY_SIZE_BALANCE_V2_END */



/* HOME_TEXT_ICON_GROUP_CENTER_V1_START */

/*
  两列布局：
  第一列是文字，第二列是图标。
  不再使用左侧透明占位。
  因此“文字 + 图标”整体会对准开始游戏按钮中心。
*/
.home-entry-list .feature-tile.vertical-entry,
.home-entry-list .feature-tile.vertical-entry:hover,
.home-entry-list .feature-tile.vertical-entry:focus-visible {
  grid-template-columns: fit-content(230px) 30px;
  grid-template-rows: 1fr;
  justify-content: center;
  align-items: center;
  column-gap: 10px;
}

/* 文字放在第一列 */
.home-entry-list .feature-entry-label {
  grid-column: 1;
  grid-row: 1;

  width: auto;
  max-width: 230px;
  justify-self: end;
  align-self: center;

  text-align: center;
}

/* 图标放在文字右边的第二列 */
.home-entry-list .feature-icon {
  grid-column: 2;
  grid-row: 1;

  width: 30px;
  height: 30px;
  min-width: 30px;
  min-height: 30px;

  justify-self: start;
  align-self: center;
}

/* 窄屏幕使用对应的小尺寸 */
@media (max-width: 380px) {
  .home-entry-list .feature-tile.vertical-entry,
  .home-entry-list .feature-tile.vertical-entry:hover,
  .home-entry-list .feature-tile.vertical-entry:focus-visible {
    grid-template-columns: fit-content(200px) 28px;
    column-gap: 9px;
  }

  .home-entry-list .feature-entry-label {
    max-width: 200px;
  }

  .home-entry-list .feature-icon {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
  }
}

/* HOME_TEXT_ICON_GROUP_CENTER_V1_END */



/* HOME_ENTRY_LIST_OFFSET_V2_START */

/*
  只移动五个入口，不参与主页整体布局计算。
  游戏名称和开始游戏按钮不会移动。
*/
.home-entry-list {
  transform: translateY(26px);
}

/* 当前第四项是皮肤商店，皮肤图标保持加宽 */
.home-entry-list .vertical-entry:nth-child(4) .feature-icon svg {
  transform: scaleX(1.25) scaleY(1);
  transform-origin: center;
}

/* 当前第三项是全球排名，恢复排行榜图标正常比例 */
.home-entry-list .vertical-entry:nth-child(3) .feature-icon svg {
  transform: scale(1.08);
  transform-origin: center;
}

/* HOME_ENTRY_LIST_OFFSET_V2_END */



/* HOME_ENTRY_HORIZONTAL_SPACING_V1_START */

.home-entry-list {
  /*
    字与字之间的横向距离。
    数值越大，文字排列越松。
  */
  --home-entry-letter-spacing: -1px;

  /*
    文字与右侧图标之间的横向距离。
    数值越大，图标距离文字越远。
  */
  --home-entry-icon-gap: 4px;
}

/* 修改文字与图标之间的距离 */
.home-entry-list .feature-tile.vertical-entry,
.home-entry-list .feature-tile.vertical-entry:hover,
.home-entry-list .feature-tile.vertical-entry:focus-visible {
  column-gap: var(--home-entry-icon-gap);
}

/* 修改每个汉字、字母之间的距离 */
.home-entry-list .feature-entry-label {
  letter-spacing: var(--home-entry-letter-spacing);
}

/* HOME_ENTRY_HORIZONTAL_SPACING_V1_END */

/* HOME_THREE_AREA_OFFSETS_V1_START */

.home {
  /*
    参数一：游戏名称的垂直位置。
    正数向下，负数向上。
  */
  --home-title-offset-y: 9px;

  /*
    参数二：开始游戏按钮的垂直位置。
    正数向下，负数向上。
  */
  --home-play-offset-y: 25px;

  /*
    参数三：下方五个入口的垂直位置。
    正数向下，负数向上。
  */
  --home-entry-offset-y: 80px;

  /*
    下方五个入口的水平位置。
  */
  --home-entry-offset-x: -3px;
}

/* 只移动游戏名称 */
.hero h1 {
  transform: translateY(
    var(--home-title-offset-y)
  );
}

/*
  移动开始游戏区域。
  不修改home-content，避免带动其他内容。
*/
.primary-action {
  transform: translateY(
    var(--home-play-offset-y)
  );
}

/* 单独移动下方五个入口 */
.home-entry-list {
  transform: translate(
    var(--home-entry-offset-x),
    var(--home-entry-offset-y)
  );
}

/* HOME_THREE_AREA_OFFSETS_V1_END */

/* HOME_TITLE_FONT_SIZE_V1_START */

/*
  游戏名称字体大小：
  52px = 最小字号
  13vw = 跟随屏幕宽度变化
  76px = 最大字号
*/
.hero h1 {
  font-size: clamp(52px, 16vw, 76px);
}

/* HOME_TITLE_FONT_SIZE_V1_END */

/* HOME_PLAY_BUTTON_SIZE_V1_START */

/* 放大开始游戏按钮整体区域 */
.play-button {
  width: 250px;
  height: 84px;
  padding: 0 26px;
  gap: 16px;

  /*
    继续保持椭圆按钮形状。
    如果以后想改成圆角矩形，可以把999px改成20px。
  */
  border-radius: 999px;
}

/* 放大“开始游戏”文字 */
.play-label {
  font-size: clamp(26px, 7vw, 33px);
  font-weight: 900;
  line-height: 1;
}

/* 放大文字左侧的播放三角形 */
.play-icon {
  border-top-width: 10px;
  border-bottom-width: 10px;
  border-left-width: 18px;
}

/* 较小屏幕使用稍小尺寸，避免超出页面 */
@media (max-width: 380px) {
  .play-button {
    width: 220px;
    height: 78px;
    padding: 0 23px;
    gap: 14px;
  }

  .play-label {
    font-size: clamp(25px, 7vw, 30px);
  }

  .play-icon {
    border-top-width: 9px;
    border-bottom-width: 9px;
    border-left-width: 16px;
  }
}

/* HOME_PLAY_BUTTON_SIZE_V1_END */
</style>
